/**
 * A tiny ring buffer of client-side failures for Jam's metadata
 * (`jam-metadata.ts`): uncaught errors, unhandled rejections and failed RPC
 * calls. Names, timings and codes only — never a message, stack, input or
 * response, so nothing here can leak content into a bug report.
 */

export type ClientEventKind = 'error' | 'rejection' | 'rpc'

export interface ClientEvent {
  kind: ClientEventKind
  /** Error class (`TypeError`) or the RPC path (`chat.triggerAgent`). */
  id: string
  /** oRPC error code (`UNAUTHORIZED`) or HTTP-ish status for RPC failures. */
  code?: string
  /** Epoch ms when it happened. */
  at: number
  /** How long the failed call took (RPC only). */
  durationMs?: number
}

/** How many events the buffer keeps; the metadata exposes the newest few. */
export const CLIENT_EVENT_CAPACITY = 20

const buffer: ClientEvent[] = []

export function recordClientEvent(event: ClientEvent): void {
  buffer.push(event)
  if (buffer.length > CLIENT_EVENT_CAPACITY) buffer.splice(0, buffer.length - CLIENT_EVENT_CAPACITY)
}

/** The newest `limit` events, oldest first. */
export function recentClientEvents(limit: number): ClientEvent[] {
  return buffer.slice(Math.max(0, buffer.length - limit))
}

/** Test helper. */
export function clearClientEvents(): void {
  buffer.length = 0
}

function errorName(value: unknown): string {
  if (value instanceof Error) return value.name || 'Error'
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { name?: unknown }).name === 'string'
  ) {
    return (value as { name: string }).name
  }
  return 'Error'
}

let installed = false

/**
 * Listen for uncaught errors and unhandled rejections on the window. Called
 * once from the app root; safe to call again.
 */
export function installClientEventCapture(target: Window = window): void {
  if (installed) return
  installed = true
  target.addEventListener('error', (event) => {
    recordClientEvent({ kind: 'error', id: errorName(event.error), at: Date.now() })
  })
  target.addEventListener('unhandledrejection', (event) => {
    recordClientEvent({ kind: 'rejection', id: errorName(event.reason), at: Date.now() })
  })
}
